import { useTheme } from '@mui/material'
import { PieChartComponent } from '../../../common/components/PieChartComponent/PieChartComponent'
import { useStyles } from './RevenueByProductChart.styles'
import React from 'react'
import { PieCustomTooltip } from './PieCustomTooltip/PieCustomTooltip'
import { PaletteColor } from '@mui/material'
import { CustomLegends } from '../CustomLegends/CustomLegends'
import clsx from 'clsx'
import { numberFormat } from '@labrav/common'
import { useSelector } from '../../../store/hooks'
import { Overview } from '../../../modules/flyoutOverview/types'
import { OverviewEmptyState } from '../OverviewEmptyState/OverviewEmptyState'
import OverviewEmptyStateImage from '../../../common/images/overviewEmptyState.png'
import { getCloudImg } from '../RevenueOverviewChart/RevenueOverviewChart'

export type PieDataInfo = {
  id: string
  label: string
  color: PaletteColor
  value: number
  percentage?: number
  cloud?: string
}
export const getColorForProduct = (index: number) => {
  const theme = useTheme()
  if (index === 0) return theme.palette.awsGraph.main
  if (index === 1) return theme.palette.gcpGraph.main
  if (index === 2) return theme.palette.otherGraph.main
  return theme.palette.microsoftGraph.main
}
export const RevenueByProductChart = () => {
  const { classes } = useStyles()
  const theme = useTheme()

  const { revenue } = useSelector<Overview>(
    state => state.flyoutOverview?.overview
  )
  const isRevenueByProductDataEmpty =
    !revenue?.revenueByProduct || revenue.revenueByProduct.length === 0

  const revenueByProductData = revenue?.revenueByProduct ?? []
  const datapie = revenueByProductData.slice(0, 3)
  const otherProducts = revenueByProductData[3]?.otherProducts ?? []
  const otherProductsTotalValue = revenueByProductData[3]?.value || 0
  const otherProductsTotalPercentage = revenueByProductData[3]?.percentage || 0

  const topProducts = [
    ...datapie
      .filter(item => item.value !== 0)
      .map((item, index) => ({
        id: item.name,
        label: item.name,
        value: item.value,
        color: getColorForProduct(index),
      })),
    ...(otherProductsTotalValue !== 0
      ? [
          {
            id: 'Other',
            label: 'Other Products',
            value: otherProductsTotalValue,
            color: getColorForProduct(3),
          },
        ]
      : []),
  ]

  const percentageData: Record<string, number> = {
    ...datapie.reduce(
      (acc, item) => ({
        ...acc,
        [item.name]: item.percentage,
      }),
      {} as Record<string, number>
    ),
    Other: otherProductsTotalPercentage,
  }

  const transformedOtherProducts = otherProducts
    .filter(item => item.value !== 0)
    .map(item => ({
      id: item.name,
      label: item.name,
      value: item.value,
      cloud: item.cloud,
      percentage: otherProductsTotalPercentage,
      color: theme.palette.awsGraph,
    }))

  const customLegendsData = [
    ...datapie
      .filter(item => item.value !== 0)
      .map((item, index) => ({
        label: item.name,
        legendColor: getColorForProduct(index),
        legendSize: 'medium' as const,
        value: `USD ${numberFormat(item.value)}`,
        isKeyValue: true,
        icon: (
          <img
            src={getCloudImg(item.cloud)}
            className={clsx(classes.imgIcon, classes.awsIcon)}
          />
        ),
      })),
    ...(otherProductsTotalValue !== 0
      ? [
          {
            label: 'Other Products',
            legendColor: theme.palette.microsoftGraph.main,
            legendSize: 'medium' as const,
            value: `USD ${numberFormat(otherProductsTotalValue)}`,
            isKeyValue: true,
          },
        ]
      : []),
  ]

  return (
    <>
      {isRevenueByProductDataEmpty ? (
        <OverviewEmptyState
          imgUrl={OverviewEmptyStateImage}
          containerHeight={17.5}
          imageHeight={12}
          imageWidth={16.75}
          description="No revenue has been generated by your product(s)."
        />
      ) : (
        <div
          className={classes.responsivePieWrapper}
          data-testid={'revenue-by-product-chart'}
        >
          <div className={classes.leftContainer}>
            <PieChartComponent
              data={topProducts}
              height="184px"
              width="176px"
              colors={[
                theme.palette.awsGraph.main,
                theme.palette.gcpGraph.main,
                theme.palette.otherGraph.main,
                theme.palette.microsoftGraph.main,
              ]}
              tooltipContent={point => (
                <PieCustomTooltip
                  preFix="USD"
                  percentage={percentageData[point?.datum?.data.id]}
                  otherProducts={transformedOtherProducts}
                  point={point}
                />
              )}
            />
          </div>
          <div className={classes.rightContainer}>
            <CustomLegends flexDirection="column" legends={customLegendsData} />
          </div>
        </div>
      )}
    </>
  )
}
